import { useMemo } from 'react';
import type { PrimaryCountryISO } from '@/constants/countries';
import useIndustriesFetch from '@/pages/Market/hooks/useIndustriesFetch';

interface Industry {
  readonly id: number;
  readonly name: string;
  readonly level: string;
  readonly slug: string;
  readonly children: readonly number[];
}

interface UseIndustryPathResult {
  readonly flattened: readonly Industry[];
  readonly path: readonly Industry[];
}

export default function useIndustryPath(
  targetIndustryId: number,
  market: PrimaryCountryISO
): UseIndustryPathResult {
  const { industries } = useIndustriesFetch(market, false);

  return useMemo(() => {
    const flattened: Industry[] = [];
    const path: Industry[] = [];
    for (const level in industries) {
      for (const industryId in industries[level]) {
        const industry = industries[level][industryId];

        flattened.push({
          id: parseInt(industryId),
          level,
          slug: industry.slug,
          name: industry.label,
          children: industry.children?.map((id: string) => parseInt(id)) ?? [],
        });
      }
    }
    if (targetIndustryId === 0) {
      return { path, flattened };
    }

    const targetIndustry = flattened.find(
      (industry) => industry.id === targetIndustryId
    );

    if (targetIndustry) {
      path.push(targetIndustry);

      const findParent = (id: number) =>
        flattened.find((industry) =>
          industry.children.some((child) => id === child)
        );

      let parent = findParent(targetIndustryId);

      while (parent) {
        path.push(parent);
        parent = findParent(parent.id);
      }
    }

    return { flattened, path: path.reverse() };
  }, [targetIndustryId, industries]);
}
